import { useTranslation } from './i18n';
import { useCreditsSummary, type CreditsSummary } from '../components/symphony/sidebar/hooks/use-credits-summary';
import { useCreditsWidgetSim, simIsMaxPlus } from './credits-widget-sim';
import { useCreditsWidgetState } from './use-credits-widget-state';
import type { ClickModifiers } from './use-in-app-link';

/**
 * The low/out-of-credits widgets' one button, decided in ONE place so the drawer card, the banner and
 * the chat widget can never send a user somewhere different.
 *
 * It REUSES the drawer credits card's own action (`useCreditsSummary().upgrade`) — same destination
 * (the package picker) and same top-up modal wiring — and only swaps the visible LABEL to the widget's
 * copy, by tier: a daily/FREE account reads "Upgrade", a monthly/PAID account reads "Upgrade Plan", and
 * the top ("Max+") tier — whose option carries a top-up pack, so it lands on the `topup` arm — reads
 * "Add Credits". Reusing the action keeps the top-up lookup and the picker origin single-sourced with
 * the card; the label is the only thing the design changes here.
 *
 * `null` only when the summary itself is unavailable (balance unknown / no metasite) — which is exactly
 * when `useCreditsWidgetState` is `none`, so a rendering widget always has a CTA.
 */
export interface CreditsWidgetCta {
  /** `topup` = the Max+ top-up modal ("Buy more credits"); `packages` = the picker ("Upgrade"). */
  kind: CreditsSummary['upgrade']['kind'];
  label: string;
  /** The picker link, present only on the `packages` arm (so cmd-click / open-in-new-tab work). */
  href: string | null;
  path: string | null;
  onClick: (event?: ClickModifiers) => void;
  /** What `BuyCreditsModal` needs; non-null exactly when `kind === 'topup'`. */
  buyCredits: CreditsSummary['buyCredits'];
}

/**
 * A PLACEHOLDER top-up pack for the dev Max+ simulator only — fake amounts, so the sim can open the
 * real `BuyCreditsModal` and preview the top-up flow on an account that has no real pack. Dev-only: it
 * is reached solely through `simIsMaxPlus`, which is null without a dev-tools grant.
 */
const SIM_MAXPLUS_TOPUP = { topup: { packId: 'sim-maxplus-topup', credits: 3000, amount: 30, currency: 'usd' }, planName: 'Max+' };

export function useCreditsWidgetCta(): CreditsWidgetCta | null {
  const { t } = useTranslation();
  const summary = useCreditsSummary();
  const sim = useCreditsWidgetSim();
  const state = useCreditsWidgetState();
  if (!summary) return null;
  const { upgrade, buyCredits } = summary;

  // The label follows the tier: Max+ (whenever the button opens the top-up modal — a real pack or the
  // dev Max+ preview below) reads "Add Credits"; a FREE account (daily, or a free plan metered monthly)
  // reads "Upgrade"; a PAID account reads "Upgrade Plan". Basing the top-up arm on the resolved action
  // keeps a Max+ account with no pack (which falls to the picker) on a sane "Upgrade Plan".
  const simPreviewTopup = simIsMaxPlus(sim) && upgrade.kind !== 'topup';
  const isTopup = upgrade.kind === 'topup' || simPreviewTopup;
  const label = isTopup
    ? t('credits.widget.cta.addCredits')
    : state.isFreePlan
      ? t('credits.widget.cta.upgrade')
      : t('credits.widget.cta.upgradePlan');

  // Dev Max+ sim on a non-Max+ account: there is no real top-up pack, so open the BuyCreditsModal with
  // the placeholder pack above (fake amounts; Check out points at the picker) purely to preview the
  // Max+ top-up flow. A real Max+ account already has `upgrade.kind === 'topup'` and takes the path
  // below with its real pack.
  if (simPreviewTopup) {
    return {
      kind: 'topup',
      label,
      href: null,
      path: null,
      onClick: () => {}, // preview only — no BI / navigation, just open the modal
      buyCredits: { ...SIM_MAXPLUS_TOPUP, checkoutHref: upgrade.kind === 'packages' ? upgrade.path : '/packages' },
    };
  }

  return {
    kind: upgrade.kind,
    label,
    href: upgrade.kind === 'packages' ? upgrade.route : null,
    path: upgrade.kind === 'packages' ? upgrade.path : null,
    onClick: upgrade.onClick,
    buyCredits,
  };
}
